import { useMemo, useState } from 'react';
import { Search, Loader2, Car as CarIcon } from 'lucide-react';
import { Input } from '../components/ui/input';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { useMerchantMarket } from '../hooks/useMerchantMarket';
import MarketHero from '../components/marketplace/MarketHero';
import MarketFilterShell from '../components/marketplace/MarketFilterShell';
import EmptyState from '../components/marketplace/EmptyState';
import CarCard from '../components/marketplace/CarCard';
import { pageBg, inputCls, selectCls } from '../components/marketplace/frames';

export default function CarsPage() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const { items, loading, error } = useMerchantMarket('auto', { limit: 300 });
  const [q, setQ] = useState('');
  const [condition, setCondition] = useState('all');
  const [sort, setSort] = useState('newest');

  const filtered = useMemo(() => {
    let list = items;
    if (condition !== 'all') list = list.filter(i => i.condition === condition);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter(i =>
        (i.make || '').toLowerCase().includes(s) ||
        (i.model || '').toLowerCase().includes(s) ||
        String(i.year || '').includes(s) ||
        (i.store?.name || '').toLowerCase().includes(s),
      );
    }
    if (sort === 'newest')          list = [...list].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    else if (sort === 'price_asc')  list = [...list].filter(i => !i.priceOnRequest).sort((a, b) => (a.salePrice || a.price || 0) - (b.salePrice || b.price || 0));
    else if (sort === 'price_desc') list = [...list].filter(i => !i.priceOnRequest).sort((a, b) => (b.salePrice || b.price || 0) - (a.salePrice || a.price || 0));
    return list;
  }, [items, q, condition, sort]);

  const hasAnyFilter = q || condition !== 'all' || sort !== 'newest';
  const clearAll = () => { setQ(''); setCondition('all'); setSort('newest'); };
  const sel = selectCls(isDarkMode);

  return (
    <div className={`min-h-screen ${pageBg(isDarkMode)}`} dir={isAr ? 'rtl' : 'ltr'}>
      <MarketHero
        isDarkMode={isDarkMode}
        isAr={isAr}
        icon={CarIcon}
        eyebrowAr="سيارات موثّقة من تجار معتمدين"
        eyebrowEn="Verified cars from certified dealers"
        titleAr="سيارات"
        titleAccentAr="مميزة للبيع"
        titleEn="Cars"
        titleAccentEn="for Sale"
        subtitleAr="اختر بين أحدث الموديلات والسيارات المستعملة والمعتمدة من متاجر موثّقة في كل إمارات الدولة — ودع CGM AI يرشّح لك الأنسب."
        subtitleEn="Choose from the latest new, pre-owned and certified vehicles listed by verified dealers across the UAE — and let CGM AI shortlist the best fit."
        aiPromptAr="ساعدني في اختيار سيارة مناسبة لميزانيتي واحتياجاتي"
        aiPromptEn="Help me choose a car that fits my budget and needs"
        stats={[
          { value: items.length || '—', label: isAr ? 'سيارة متاحة' : 'Available', icon: <CarIcon className="h-4 w-4" /> },
          { value: '7', label: isAr ? 'إمارات' : 'Emirates', accent: '#F5A962' },
          { value: '✓', label: isAr ? 'معتمد' : 'Certified', accent: '#1E9BD9' },
        ]}
      />

      <MarketFilterShell
        isDarkMode={isDarkMode}
        isAr={isAr}
        count={filtered.length}
        total={items.length}
        unitAr="سيارة"
        unitEn="cars"
        hasFilter={hasAnyFilter}
        onClear={clearAll}
        testId="cars-filters"
      >
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-[#1E9BD9]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={isAr ? 'الشركة، الموديل، السنة…' : 'Make, model, year…'} className={`h-11 ps-9 rounded-xl ${inputCls(isDarkMode)}`} data-testid="cars-search" />
          </div>
          <select value={condition} onChange={(e) => setCondition(e.target.value)} className={sel} data-testid="cars-condition">
            <option value="all">{isAr ? 'الحالة: الكل' : 'Condition: Any'}</option>
            <option value="new">{isAr ? 'جديدة' : 'New'}</option>
            <option value="used">{isAr ? 'مستعملة' : 'Used'}</option>
            <option value="certified">{isAr ? 'معتمدة' : 'Certified'}</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className={sel} data-testid="cars-sort">
            <option value="newest">{isAr ? 'الأحدث' : 'Newest'}</option>
            <option value="price_asc">{isAr ? 'الأرخص أولاً' : 'Price ↑'}</option>
            <option value="price_desc">{isAr ? 'الأغلى أولاً' : 'Price ↓'}</option>
          </select>
        </div>
      </MarketFilterShell>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-20">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" /></div>
        ) : error ? (
          <p className="text-center text-red-500 py-24">{error}</p>
        ) : filtered.length === 0 ? (
          <EmptyState isDarkMode={isDarkMode} icon={CarIcon} title={isAr ? 'لا توجد سيارات مطابقة' : 'No matching cars'} hint={isAr ? 'جرّب تغيير الفلاتر أو البحث بكلمات أخرى' : 'Try adjusting filters or search terms'} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="cars-grid">
            {filtered.map((it, idx) => <CarCard key={it.id} item={it} index={idx} isDarkMode={isDarkMode} isAr={isAr} />)}
          </div>
        )}
      </section>
    </div>
  );
}
